import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import RefreshIcon from "@mui/icons-material/Refresh"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { ReactComponent as TrashIcon } from "src/v2/assets/icons/trash-icon.svg"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import ButtonWithIcon from "src/v2/components/Button/Button"
import DuplaConfirmacao from "src/v2/components/DuplaConfirmacao/DuplaConfirmacao"
import theme from "src/v2/theme/theme"
import { COMPANY_HISTORY_CONFIG, formatHistoryValue } from "./company-history-config"
import CompanyRegistrationForm from "../registrations/company-registration-form"
import { normalizeExternalHttpUrl } from "src/v2/utils/normalize-href"
import useCompanyHistory from "./use-company-history"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import { formatCpfCnpj } from "src/v2/utils/formatter/format-cpf-cnpj"
import CompanyMetadataContext from "../company-metadata-context"

const HistoryError = ({ message, onRetry }) => <Box sx={{ display: "grid", justifyItems: "center", gap: 2 }}><Typography color="common.white" role="alert">{message}</Typography><ButtonWithIcon icon={<RefreshIcon />} onClick={onRetry}>TENTAR NOVAMENTE</ButtonWithIcon></Box>

HistoryError.propTypes = { message: PropTypes.string, onRetry: PropTypes.func.isRequired }

const companyContextFields = [
    { key: "RazaoSocial", label: "RAZÃO SOCIAL" },
    { key: "CPFCNPJ", label: "CNPJ" },
    { key: "Setor", label: "SETOR" },
    { key: "SubSetor", label: "SUBSETOR" },
]

const getCompanyContextFields = (company) => companyContextFields.map(({ key, label }) => ({
    label,
    value: key === "CPFCNPJ" ? formatCpfCnpj(company?.[key]) || "-" : company?.[key] ?? "-",
}))

const DRAFT_ROW_ID = "new-history-row"

const isDraftRow = (row) => row?.id === DRAFT_ROW_ID

const isEmptyValue = (value) => value === null || value === undefined || (typeof value === "string" && !value.trim())

const getRequiredFieldLabels = (fields, row) => fields
    .filter((field) => field.required && isEmptyValue(row?.[field.name]))
    .map((field) => field.label)

const hasRowChanged = (newRow, oldRow) => Object.keys({ ...newRow, ...oldRow })
    .some((field) => newRow[field] !== oldRow[field])

const INLINE_EDITING_SX = {
    "& .MuiDataGrid-cell--editing": {
        backgroundColor: theme.palette.input.dynamicTextarea.background.default,
        border: `1px solid ${theme.palette.input.borderColor.primary}`,
    },
    "& .MuiDataGrid-cell--editing .MuiInputBase-input": {
        color: theme.palette.common.white,
        WebkitTextFillColor: theme.palette.common.white,
    },
}

const HISTORY_TABLE_SX = {
    "& .MuiDataGrid-columnHeaderTitle": {
        fontSize: "14px",
        lineHeight: "20px",
    },
}

const CompanyHistoryModal = ({ open, historyType, company, isAdminUser, onClose, onChanged }) => {
    const definition = COMPANY_HISTORY_CONFIG[historyType]
    const history = useCompanyHistory({ definition, company, onChanged })
    const [rowToDelete, setRowToDelete] = useState(null)
    const [viewer, setViewer] = useState(null)
    const [draftRow, setDraftRow] = useState(null)
    const canEdit = Boolean(isAdminUser && definition?.editable)
    const usesInlineEditing = definition?.editable?.interaction?.mode === "inline"
    const editableFields = definition?.editable?.fields ?? []
    const editableFieldNames = useMemo(
        () => new Set(editableFields.map((field) => field.name)),
        [editableFields]
    )
    const contextFields = getCompanyContextFields(company)

    useEffect(() => {
        setDraftRow(null)
    }, [company?.CPFCNPJ, historyType])

    const currentDraftRow = draftRow?.CPFCNPJ === company?.CPFCNPJ ? draftRow : null
    const hasDraftRow = Boolean(currentDraftRow)

    const handleClose = () => {
        setRowToDelete(null)
        setViewer(null)
        setDraftRow(null)
        onClose()
    }

    const handleAdd = async () => {
        if (history.rows.length) {
            await history.mutate({
                method: "post",
                row: definition.editable.createFromLatest({ company, rows: history.rows }),
            })
            return
        }

        setDraftRow({
            ...definition.editable.createFromLatest({ company, rows: [] }),
            id: DRAFT_ROW_ID,
        })
    }

    const handleInlineRowUpdate = useCallback(async (newRow, oldRow) => {
        if (isDraftRow(newRow)) {
            setDraftRow(newRow)
            return newRow
        }

        if (!hasRowChanged(newRow, oldRow)) return oldRow

        const saved = await history.mutate({ method: "patch", row: newRow })
        return saved ? newRow : oldRow
    }, [history.mutate])

    const handleSaveDraft = useCallback(async (row) => {
        const requiredLabels = getRequiredFieldLabels(editableFields, row)
        if (requiredLabels.length) {
            dispatchSnackbar(`Preencha os campos obrigatórios: ${requiredLabels.join(", ")}.`, "error")
            return
        }

        const saved = await history.mutate({ method: "post", row })
        if (saved) setDraftRow(null)
    }, [editableFields, history.mutate])

    const handleDelete = async () => {
        const deleted = await history.mutate({ method: "delete", row: rowToDelete })
        if (deleted) setRowToDelete(null)
    }

    const rows = currentDraftRow ? [currentDraftRow, ...history.rows] : history.rows
    const isTableLoading = history.loading && rows.length === 0

    const columns = useMemo(() => {
        if (!definition) return []

        const baseColumns = definition.columns
            .filter((column) => !column.adminOnly || isAdminUser)
            .map((column) => ({
                ...column,
                type: usesInlineEditing && column.type === "date" ? "string" : column.type,
                editable: Boolean(canEdit && usesInlineEditing && editableFieldNames.has(column.field)),
                minWidth: column.minWidth ?? 150,
                flex: column.flex ?? (column.minWidth ? 1.5 : 1),
                valueFormatter: column.type === "link"
                    ? undefined
                    : column.valueFormatter ?? (({ value }) => formatHistoryValue(value, column.type)),
                renderCell: column.type === "link" ? ({ value }) => {
                    const normalizedHref = normalizeExternalHttpUrl(value)

                    return normalizedHref ? <IconButton aria-label="Abrir documento do fato relevante" component="a" href={normalizedHref} target="_blank" rel="noopener noreferrer" sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><OpenInNewIcon fontSize="small" /></IconButton> : "-"
                } : undefined,
            }))

        const viewColumn = definition.view ? [{
            field: "visualizar",
            headerName: "Ações",
            width: 160,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => <ButtonWithIcon icon={<VisibilityIcon fontSize="small" />} aria-label={`Visualizar ${definition.title}`} onClick={() => setViewer(row)}>VISUALIZAR</ButtonWithIcon>,
        }] : []

        if (!canEdit) return [...baseColumns, ...viewColumn]

        return [...baseColumns, ...viewColumn, {
            field: "acoes",
            headerName: "Ações",
            width: usesInlineEditing && hasDraftRow ? 152 : usesInlineEditing ? 176 : 64,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => isDraftRow(row) ? <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Salvar novo registro"><IconButton aria-label="Salvar novo registro" onClick={() => handleSaveDraft(row)} disabled={history.loading} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><CheckIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Descartar novo registro"><IconButton aria-label="Descartar novo registro" onClick={() => setDraftRow(null)} disabled={history.loading} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><CloseIcon fontSize="small" /></IconButton></Tooltip>
            </Box> : usesInlineEditing ? <ButtonWithIcon icon={<TrashIcon />} onClick={() => setRowToDelete(row)}>EXCLUIR</ButtonWithIcon> : <Tooltip title="Excluir registro"><IconButton aria-label="Excluir registro" onClick={() => setRowToDelete(row)} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>,
        }]
    }, [canEdit, definition, editableFieldNames, handleSaveDraft, hasDraftRow, history.loading, isAdminUser, usesInlineEditing])

    const isCellEditable = useCallback(
        (params) => canEdit && !history.loading && editableFieldNames.has(params.field),
        [canEdit, editableFieldNames, history.loading]
    )

    if (!definition) return null

    const historyTable = <Box sx={{ height: "clamp(320px, 58dvh, 620px)", minHeight: 0 }}>
        <DataGridTable contained showPagination rows={rows} columns={columns} loading={isTableLoading} getRowId={(row) => row.id ?? row.ID} editMode={usesInlineEditing ? "cell" : undefined} isCellEditable={usesInlineEditing ? isCellEditable : undefined} processRowUpdate={usesInlineEditing ? handleInlineRowUpdate : undefined} sx={{ ...HISTORY_TABLE_SX, ...(usesInlineEditing ? INLINE_EDITING_SX : {}) }} />
    </Box>

    return <>
        <Dialog open={open} onClose={history.loading ? undefined : handleClose} fullWidth maxWidth={false} PaperProps={{ sx: { m: { xxxs: 0, sm: 2, md: 4 }, width: { xxxs: "100vw", sm: "calc(100vw - 32px)", md: "min(1480px, calc(100vw - 64px))" }, maxWidth: "none", maxHeight: { xxxs: "100dvh", sm: "calc(100dvh - 32px)" }, backgroundColor: theme.palette.background.paper, color: theme.palette.common.white } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: { xxxs: 2, sm: 3 }, pt: { xxxs: 2, sm: 3 } }}>
                <Typography component="div" variant="h3" color="common.white">{definition.title}</Typography>
                <IconButton aria-label={`Fechar ${definition.title}`} onClick={handleClose} disabled={history.loading} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: { xxxs: 2, sm: 3 }, pb: { xxxs: 2, sm: 3 }, overflow: "hidden" }}>
                <Box sx={{ display: "grid", gap: 1.5, mb: 2 }}>
                    <CompanyMetadataContext ariaLabel="Identificação do devedor" fields={contextFields} />
                    {canEdit && <Box sx={{ justifySelf: "start" }}><ButtonWithIcon icon={<AddIcon />} onClick={handleAdd} disabled={history.loading || history.status === "error" || Boolean(currentDraftRow)}>{definition.editable.interaction?.addLabel ?? "ADICIONAR NOVO"}</ButtonWithIcon></Box>}
                </Box>
                {history.status === "error" ? <Box sx={{ minHeight: 260, display: "grid", placeItems: "center" }}><HistoryError message={history.error} onRetry={history.load} /></Box> : history.status === "empty" && !currentDraftRow ? (
                    <Box sx={{ minHeight: 260, display: "grid", placeItems: "center", textAlign: "center", px: 2 }}>
                        <Typography color="common.white">{definition.emptyText}</Typography>
                    </Box>
                ) : (
                    historyTable
                )}
            </DialogContent>
        </Dialog>
        {viewer && (
            <CompanyRegistrationForm
                open
                definition={{ ...definition, fields: definition.view.fields }}
                initialRow={viewer}
                isNew={false}
                mode={definition.view.mode}
                saving={false}
                onClose={() => setViewer(null)}
                onSave={() => false}
            />
        )}
        <DuplaConfirmacao open={Boolean(rowToDelete)} title="Excluir registro" description="Esta ação não pode ser desfeita. Deseja excluir este registro?" cancel={() => setRowToDelete(null)} confirm={handleDelete} confirmTextButton={history.loading ? "EXCLUINDO..." : "EXCLUIR"} />
    </>
}

CompanyHistoryModal.propTypes = { open: PropTypes.bool.isRequired, historyType: PropTypes.oneOf(Object.keys(COMPANY_HISTORY_CONFIG)).isRequired, company: PropTypes.object, isAdminUser: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired, onChanged: PropTypes.func }
CompanyHistoryModal.defaultProps = { company: {}, onChanged: undefined }

export default CompanyHistoryModal
